import { useMemo, type ReactElement } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowLeftIcon, PencilSquareIcon, PlusIcon } from '@heroicons/react/24/outline'
import SimpleTable from 'app/components/table/SimpleTable'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from 'app/components/ui/sheet'
import { Button } from 'app/components/ui/button'
import { Badge } from 'app/components/ui/badge'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { useDatasetsPanel } from './useDatasetsPanel'
import { GoldenEditor } from './GoldenEditor'
import { GoldenStageView } from './GoldenStageView'
import { AddItemModal, NewDatasetModal } from './DatasetModals'
import { TranscriptView } from './TranscriptView'
import { formatDate, type Dataset, type DatasetItem } from './mockData'

/** Membership chips: an item's tags shown as their human dataset names. */
function DatasetChips({ names }: { names: string[] }): ReactElement {
  return (
    <div className="flex flex-wrap gap-1">
      {names.map((name) => (
        <Badge key={name} color="gray" label={name} />
      ))}
    </div>
  )
}

export function DatasetsPanelComponent(): ReactElement {
  const panel = useDatasetsPanel()
  return (
    <>
      {panel.selectedDataset ? <DatasetDetail /> : <DatasetList />}

      {panel.modal === `new-dataset` && (
        <NewDatasetModal
          onClose={panel.closeModal}
          onCreate={(input) => {
            panel.createDataset(input)
            panel.closeModal()
          }}
        />
      )}
      {panel.modal === `add-item` && (
        <AddItemModal
          defaultTag={panel.selectedDataset?.tag}
          onClose={panel.closeModal}
          onAdd={(input) => {
            panel.addItem(input)
            panel.closeModal()
          }}
        />
      )}

      <ItemSheet />
    </>
  )
}

function DatasetList(): ReactElement {
  const { datasets, itemCounts, selectDataset, openModal } = useDatasetsPanel()

  const columns = useMemo<ColumnDef<Dataset, any>[]>(
    () => [
      { accessorKey: `name`, header: `Name`, id: `Name`, meta: { defaultVisible: true } },
      {
        id: `Items`,
        header: `Items`,
        meta: { defaultVisible: true },
        accessorFn: (row) => itemCounts[row.id] ?? 0,
      },
      {
        id: `Description`,
        header: `Description`,
        enableSorting: false,
        meta: { defaultVisible: true },
        accessorKey: `description`,
      },
      {
        id: `Created`,
        header: `Created`,
        meta: { defaultVisible: true },
        cell: (props) => `${props.row.original.createdBy} · ${formatDate(props.row.original.createdAt)}`,
      },
    ],
    [itemCounts],
  )

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button variant="primary" size="sm" onClick={() => openModal(`new-dataset`)}>
          <PlusIcon className="mr-1 h-4 w-4" /> New dataset
        </Button>
      </div>
      <SimpleTable<Dataset>
        columns={columns}
        data={datasets}
        hideFooter
        meta={{ onRowClick: (row) => selectDataset(row.id) }}
        emptyState={<TableEmptyState title="No datasets yet" />}
      />
    </div>
  )
}

function DatasetDetail(): ReactElement {
  const { datasets, selectedDataset, items, clearDataset, selectItem, openModal } = useDatasetsPanel()

  // Membership is stored as tags; render them as the datasets they map to (ops-only,
  // never the user-facing Call/Contact tag system).
  const tagToName = useMemo(() => {
    const map: Record<string, string> = {}
    for (const dataset of datasets) map[dataset.tag] = dataset.name
    return map
  }, [datasets])

  const columns = useMemo<ColumnDef<DatasetItem, any>[]>(
    () => [
      {
        id: `Call date`,
        header: `Call date`,
        meta: { defaultVisible: true },
        accessorFn: (row) => formatDate(row.callDate),
      },
      { accessorKey: `callerName`, header: `Caller`, id: `Caller`, meta: { defaultVisible: true } },
      {
        id: `People`,
        header: `People`,
        meta: { defaultVisible: true },
        accessorFn: (row) => row.golden.people.length,
      },
      {
        id: `Datasets`,
        header: `Datasets`,
        size: 280,
        enableSorting: false,
        meta: { defaultVisible: true },
        cell: (props) => <DatasetChips names={props.row.original.tags.map((tag) => tagToName[tag] ?? tag)} />,
      },
      { accessorKey: `labeledBy`, header: `Labeled by`, id: `Labeled by`, meta: { defaultVisible: true } },
    ],
    [tagToName],
  )

  if (!selectedDataset) return <DatasetList />

  return (
    <div>
      <Button variant="tertiary" size="sm" className="mb-2" onClick={clearDataset}>
        <ArrowLeftIcon className="mr-1 h-4 w-4" /> All datasets
      </Button>

      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h4 className="text-lg font-semibold text-strong">{selectedDataset.name}</h4>
          <p className="mt-1 text-sm text-subtle">{selectedDataset.description}</p>
        </div>
        <Button variant="primary" size="sm" className="shrink-0" onClick={() => openModal(`add-item`)}>
          <PlusIcon className="mr-1 h-4 w-4" /> Add item
        </Button>
      </div>

      <SimpleTable<DatasetItem>
        columns={columns}
        data={items}
        hideFooter
        meta={{ onRowClick: (row) => selectItem(row.id) }}
        emptyState={<TableEmptyState title="No items in this dataset yet" />}
      />
    </div>
  )
}

function ItemSheet(): ReactElement {
  const { focusedItem, clearItem, editing, startEditing, stopEditing, updateGolden } = useDatasetsPanel()
  const goldenNames = useMemo(
    () => focusedItem?.golden.people.map((person) => person.name) ?? [],
    [focusedItem?.golden.people],
  )

  return (
    <Sheet open={focusedItem != null} onOpenChange={(open) => !open && clearItem()}>
      <SheetContent className="w-full overflow-y-auto p-6 sm:max-w-xl">
        {focusedItem && (
          <>
            <SheetHeader className="p-0">
              <SheetTitle>{focusedItem.callerName}</SheetTitle>
              <SheetDescription>
                {formatDate(focusedItem.callDate)} · labeled by {focusedItem.labeledBy}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-5">
              <TranscriptView text={focusedItem.transcriptSnapshot} names={goldenNames} />

              {editing ? (
                <GoldenEditor
                  initial={focusedItem.golden}
                  onSave={(golden) => {
                    updateGolden(focusedItem.id, golden)
                    stopEditing()
                  }}
                  onCancel={stopEditing}
                />
              ) : (
                <section>
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-xs font-semibold uppercase text-subtle">Expected extraction by stage</h4>
                    <Button variant="tertiary" size="xs" onClick={startEditing}>
                      <PencilSquareIcon className="mr-1 h-3.5 w-3.5" /> Edit
                    </Button>
                  </div>
                  <GoldenStageView golden={focusedItem.golden} />
                </section>
              )}

              <section className="text-xs text-minimal">
                Original AI extraction: {focusedItem.provenance.model} · temp {focusedItem.provenance.temperature}
              </section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
